import { CircleOff, Mail, Ban } from 'lucide-react';
import React, { useState, useEffect } from 'react';

import { useOnboardingStore } from '../../../store/onboarding.store';

interface ClassAgeAdherenceStepProps {
  onNext: () => void;
  onBack: () => void;
}

type AgeAdherencePolicy = 'open' | 'request' | 'strict';

export function ClassAgeAdherenceStep({ onNext, onBack }: ClassAgeAdherenceStepProps) {
  const { billingData, setBillingData } = useOnboardingStore();

  // Extract policy from classAgeAdherencePolicy object or default to 'request' (Invite - recommended)
  const currentPolicy = billingData.classAgeAdherencePolicy?.policy || null;
  const [selectedPolicy, setSelectedPolicy] = useState<AgeAdherencePolicy | null>(
    (currentPolicy as AgeAdherencePolicy | null) || 'request' // Default to 'request' (Invite)
  );

  useEffect(() => {
    // Always save the policy (default is 'request' if none selected)
    const policyToSave = selectedPolicy || 'request';
    setBillingData({
      classAgeAdherencePolicy: { policy: policyToSave },
    });
  }, [selectedPolicy, setBillingData]);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Class Age Adherence Policy</h2>
        <p className="text-[#D1D5DB] mt-2">
          Set the default age requirement policy for class enrollments. You can change this later in Calendar Settings.
        </p>
      </div>

      <div className="space-y-4">
        {/* Request - Invite/Request Approval (Recommended - First) */}
        <button
          onClick={() => setSelectedPolicy('request')}
          className={`w-full rounded-xl text-left transition-all overflow-hidden p-5 ${
            selectedPolicy === 'request'
              ? 'ring-2 ring-[#9B1421] ring-offset-2 ring-offset-[#221F1F] bg-gradient-to-br from-[#9B1421]/20 via-[#221F1F] to-[#221F1F]'
              : 'border-2 border-[#504A4A] hover:border-[#9B1421]/50 bg-[#221F1F]'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Mail size={48} className="text-rollops-accent" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-bold text-[#EBFFEE]">Invite</h3>
                <span className="text-xs bg-green-600/20 text-green-500 px-2 py-0.5 rounded-full font-semibold">
                  Recommended
                </span>
              </div>
              <p className="text-[#D1D5DB] text-sm mb-2">
                Require coach approval for age-ineligible registrations. Students can request
                exceptions, and coaches will receive notifications to approve or deny.
              </p>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2 mt-2">
                <p className="text-xs text-green-400">
                  <strong>Best for:</strong> Most gyms. Offers flexibility - students can request exceptions while you maintain control. Perfect balance of automation and oversight.
                </p>
              </div>
            </div>
            {selectedPolicy === 'request' && (
              <div className="w-8 h-8 rounded-full bg-[#9B1421] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm">✓</span>
              </div>
            )}
          </div>
        </button>

        {/* Open - No Restrictions */}
        <button
          onClick={() => setSelectedPolicy('open')}
          className={`w-full rounded-xl text-left transition-all overflow-hidden p-5 ${
            selectedPolicy === 'open'
              ? 'ring-2 ring-[#9B1421] ring-offset-2 ring-offset-[#221F1F] bg-gradient-to-br from-[#9B1421]/20 via-[#221F1F] to-[#221F1F]'
              : 'border-2 border-[#504A4A] hover:border-[#9B1421]/50 bg-[#221F1F]'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <CircleOff size={48} className="text-rollops-accent" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-[#EBFFEE] mb-2">No Restrictions</h3>
              <p className="text-[#D1D5DB] text-sm mb-2">
                Allow all registrations regardless of age. No restrictions or approval required. Age
                requirements can be set per class later.
              </p>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-2 mt-2">
                <p className="text-xs text-blue-400">
                  <strong>Best for:</strong> Gyms with mixed-age classes or when age isn&apos;t a concern. Minimal administrative overhead - students enroll freely.
                </p>
              </div>
            </div>
            {selectedPolicy === 'open' && (
              <div className="w-8 h-8 rounded-full bg-[#9B1421] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm">✓</span>
              </div>
            )}
          </div>
        </button>

        {/* Strict - Block Age-Ineligible */}
        <button
          onClick={() => setSelectedPolicy('strict')}
          className={`w-full rounded-xl text-left transition-all overflow-hidden p-5 ${
            selectedPolicy === 'strict'
              ? 'ring-2 ring-[#9B1421] ring-offset-2 ring-offset-[#221F1F] bg-gradient-to-br from-[#9B1421]/20 via-[#221F1F] to-[#221F1F]'
              : 'border-2 border-[#504A4A] hover:border-[#9B1421]/50 bg-[#221F1F]'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Ban size={48} className="text-rollops-accent" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-[#EBFFEE] mb-2">Strict</h3>
              <p className="text-[#D1D5DB] text-sm mb-2">
                Strictly block enrollments that don&apos;t meet age requirements. Students who don&apos;t meet
                the age requirement will be blocked from enrolling. Coaches can still manually register
                students if needed.
              </p>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2 mt-2">
                <p className="text-xs text-yellow-400">
                  <strong>Best for:</strong> Gyms with strict age-based programs (e.g., youth sports, senior classes) where age compliance is critical. Maximum enforcement with manual override available.
                </p>
              </div>
            </div>
            {selectedPolicy === 'strict' && (
              <div className="w-8 h-8 rounded-full bg-[#9B1421] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm">✓</span>
              </div>
            )}
          </div>
        </button>
      </div>

      {selectedPolicy && (
        <div className="mt-4 p-4 bg-[#9B1421]/20 border border-[#9B1421] rounded-lg">
          <p className="text-sm text-white">
            {selectedPolicy === 'open' && (
              <>You&apos;ll be able to set age requirements per class later if needed. All students can enroll in any class regardless of age.</>
            )}
            {selectedPolicy === 'request' && (
              <>Students who don&apos;t meet age requirements can request an exception. Coaches will receive notifications to approve or deny these requests.</>
            )}
            {selectedPolicy === 'strict' && (
              <>You&apos;ll be able to set age requirements (e.g., &quot;13-17 years&quot;) for each class in your class settings. Students who don&apos;t meet the age requirement will be blocked from enrolling.</>
            )}
          </p>
        </div>
      )}

      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-[#363232] text-[#EBFFEE] rounded-lg hover:bg-[#504A4A] transition-colors"
        >
          Go Back
        </button>
        <button
          onClick={onNext}
          disabled={!selectedPolicy}
          className={`px-6 py-3 rounded-lg transition-colors ${
            selectedPolicy
              ? 'bg-[#9B1421] text-white hover:bg-[#C01F2D]'
              : 'bg-[#363232] text-[#D1D5DB] cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
}







